import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Compass, SignOut, ShieldStar, Bell, UsersThree, GearSix, ChatCircleDots, CaretDown } from "@phosphor-icons/react";
import api from "../api";
import { BetaProvider, useBeta } from "../context/BetaContext";
import OnboardingWalkthrough from "./OnboardingWalkthrough";
import FeedbackModal from "./FeedbackModal";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";

export default function Layout({ children }) {
  return (
    <BetaProvider>
      <LayoutShell>{children}</LayoutShell>
    </BetaProvider>
  );
}

function LayoutShell({ children }) {
  const { user, logout } = useAuth();
  const { openFeedback } = useBeta();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/notifications/unread-count").then((r) => alive && setUnread(r.data.unread)).catch(() => {});
    poll();
    const id = setInterval(poll, 45000); // gentle poll, avoids excessive polling
    return () => { alive = false; clearInterval(id); };
  }, [loc.pathname]);

  const links = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/play", label: "Expeditions" },
    { to: "/daily", label: "Daily" },
    { to: "/rivals", label: "Rivals" },
    { to: "/cards", label: "Cards" },
    { to: "/my-seasons", label: "Seasons" },
    { to: "/prestige", label: "Prestige" },
    { to: "/camp", label: "Camp" },
  ];
  // lower-frequency destinations live in a "More" menu so the desktop header never overflows
  const moreLinks = [
    { to: "/hall-of-renown", label: "Hall" },
    { to: "/journey", label: "Journey" },
    { to: "/achievements", label: "Achievements" },
    { to: "/leaderboards", label: "Ranks" },
    { to: "/history", label: "History" },
  ];
  const moreActive = moreLinks.some((l) => loc.pathname === l.to);

  return (
    <div className="min-h-screen overflow-x-hidden bg-ff-bg text-ff-parchment">
      <header className="sticky top-0 z-40 border-b border-ff-brass/25 bg-ff-bg/90 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 py-3">
          <Link to="/dashboard" data-testid="nav-logo" className="flex shrink-0 items-center gap-2">
            <Compass size={28} weight="duotone" className="text-ff-gold animate-compass" />
            <span className="font-display text-xl font-bold tracking-tight text-ff-gold">Aurumward</span>
          </Link>

          <nav className="hidden min-w-0 flex-1 items-center gap-0.5 md:flex">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`shrink-0 whitespace-nowrap rounded-sm px-2 py-2 text-sm transition-colors duration-200 ${
                  loc.pathname === l.to ? "text-ff-gold" : "text-ff-muted hover:text-ff-parchment"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-more"
                  className={`flex shrink-0 items-center gap-1 whitespace-nowrap rounded-sm px-2 py-2 text-sm outline-none transition-colors duration-200 ${
                    moreActive ? "text-ff-gold" : "text-ff-muted hover:text-ff-parchment"
                  }`}
                >
                  More <CaretDown size={12} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-ff-brass/30 bg-ff-bg">
                {moreLinks.map((l) => (
                  <DropdownMenuItem key={l.to} asChild>
                    <Link
                      to={l.to}
                      data-testid={`nav-${l.label.toLowerCase()}`}
                      className={`cursor-pointer text-sm ${loc.pathname === l.to ? "text-ff-gold" : "text-ff-parchment"}`}
                    >
                      {l.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="flex shrink-0 items-center gap-1 rounded-sm px-3 py-2 text-sm text-ff-brass hover:text-ff-gold"
              >
                <ShieldStar size={16} weight="duotone" /> Admin
              </Link>
            )}
            {user?.role === "founder" && (
              <Link
                to="/founder"
                data-testid="nav-founder"
                className="flex shrink-0 items-center gap-1 rounded-sm px-3 py-2 text-sm text-ff-gold hover:text-ff-brass"
              >
                <ShieldStar size={16} weight="duotone" /> Founder
              </Link>
            )}
          </nav>

          <div className="flex shrink-0 items-center gap-3">
            <button
              onClick={openFeedback}
              data-testid="nav-feedback-btn"
              className="hidden items-center gap-1 rounded-full border border-ff-brass/25 px-2.5 py-1 text-[11px] uppercase tracking-wider text-ff-brass transition-colors hover:border-ff-gold hover:text-ff-gold sm:inline-flex"
            >
              <ChatCircleDots size={14} weight="duotone" /> Feedback
            </button>
            <Link to="/earn" data-testid="nav-earn" className="hidden items-center gap-1 rounded-full border border-ff-brass/25 px-2.5 py-1 text-[11px] uppercase tracking-wider text-ff-brass transition-colors hover:border-ff-gold hover:text-ff-gold lg:inline-flex">
              Earn · Coming Later
            </Link>
            <Link to="/notifications" data-testid="nav-notifications" className="relative text-ff-muted transition-colors hover:text-ff-gold">
              <Bell size={22} weight="duotone" />
              {unread > 0 && (
                <span data-testid="notif-badge" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-ff-gold px-1 text-[10px] font-bold text-ff-bg">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
            <Link to="/settings" data-testid="nav-settings" className="text-ff-muted transition-colors hover:text-ff-gold">
              <GearSix size={20} weight="duotone" />
            </Link>
            <span className="hidden text-sm text-ff-muted sm:inline" data-testid="nav-username">
              {user?.username}
            </span>
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                nav("/");
              }}
              className="flex items-center gap-1.5 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-muted transition-colors hover:border-ff-gold hover:text-ff-gold"
            >
              <SignOut size={16} /> Exit
            </button>
          </div>
        </div>
        {/* mobile nav */}
        <nav className="flex items-center gap-1 overflow-x-auto border-t border-ff-brass/15 px-4 py-2 md:hidden">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mnav-${l.label.toLowerCase()}`}
              className={`flex-1 whitespace-nowrap rounded-sm px-2 py-1.5 text-center text-xs ${
                loc.pathname === l.to ? "bg-ff-bg2 text-ff-gold" : "text-ff-muted"
              }`}
            >
              {l.label}
            </Link>
          ))}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="mnav-admin" className="flex-1 rounded-sm px-2 py-1.5 text-center text-xs text-ff-brass">
              Admin
            </Link>
          )}
          <Link to="/earn" data-testid="mnav-earn" className="flex-1 whitespace-nowrap rounded-sm px-2 py-1.5 text-center text-xs text-ff-brass">
            Earn · Soon
          </Link>
          <button onClick={openFeedback} data-testid="mnav-feedback" className="flex-1 whitespace-nowrap rounded-sm px-2 py-1.5 text-center text-xs text-ff-brass">
            Feedback
          </button>
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6">{children}</main>
      <OnboardingWalkthrough />
      <FeedbackModal />
    </div>
  );
}
